<template>
<div class="photo-gallery-modal-container">
  <!--single big image viewer-->
  <div class="single-image-container">
    <!--close button-->
    <button class="close-button" @click="emitClose">
      <span>
        <svg id="c-cross" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 13.414l6.793 6.793 1.414-1.414L13.414 12l6.793-6.793-1.414-1.414L12 10.586 5.207 3.793 3.793 5.207 10.586 12l-6.793 6.793 1.414 1.414L12 13.414z"
            fill="currentColor" />
        </svg>
      </span>
    </button>
    <div class="gallery-slider-container" v-if="list.length > 0">
      <div class="gallery-slider-wrapper">
        <button class="next-button" @click="goToNext" />
        <button class="previous-button" @click="goToPrevious" />
        <VueSlickCarousel ref="galleryImg" v-bind="imageSettings" @beforeChange="onImgBeforeChange" @init="onImgInit">
          <div v-for="(image, idx) in list" :key="idx">
            <TourGalleryImg :image="image" />
          </div>
        </VueSlickCarousel>
      </div>
      <div class="gallery-thumbnail-wrapper">
        <VueSlickCarousel ref="galleryImgThumbnail" v-bind="thumbnailSettings" @beforeChange="onBeforeChange">
          <div v-for="(image, idx) in list" :key="idx">
            <TourGalleryThumbnail class="thumbnail-nav" :image="image" :selected="idx === curIndex" />
          </div>
        </VueSlickCarousel>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { mapGetters } from 'vuex'
import VueSlickCarousel from 'vue-slick-carousel'
import 'vue-slick-carousel/dist/vue-slick-carousel.css'

import TourGalleryImg from "@/components/containers/tour/TourGalleryImg"
import TourGalleryThumbnail from "@/components/containers/tour/TourGalleryThumbnail"

const defaultSetting = {
  infinite: true,
  dots: false,
  arrows: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  centerMode: false,
  draggable: true,
  focusOnSelect: false,
  touchThreshold: 15,
  waitForAnimate: false, // fix WLK-6714 - clicks quickly bug
  accessibility: false // fix WLK-6714 - clicks quickly bug
}
const thumbnailWidth = 130
const thumbnailMobile = 100
export default {
  name: 'GallerySlider',
  components: {
    VueSlickCarousel,
    TourGalleryImg,
    TourGalleryThumbnail
  },
  props: {
    list: {
      type: Array,
      required: true
    },
    initialIndex : {
      type: Number,
      required: false,
      default: () => 0
    },
    customSettings: {
      type: Object,
      required: false,
      default: () => {}
    }
  },
  data () {
    return {
      curIndex: this.initialIndex,
      screenChanged: false,
      imageSettings: {
        ...defaultSetting,
        initialSlide: this.initialIndex
      },
      thumbnailSettings: {
        ...defaultSetting,
        focusOnSelect: true,
        slidesToShow: 8,
        initialSlide: this.initialIndex,
        draggable: true,
        touchMove: true,
        centerMode: true,
        centerPadding: '10px'
      },
      isTransitioning: false // fix WLK-6714 - clicks quickly bug
    }
  },
  computed: {
    ...mapGetters([
      'screenWidth'
    ]),
    thumbnailsToShow () {
      const imgWidth = this.$refs?.galleryImg?.$el.clientWidth ?? this.screenWidth
      if (this.screenWidth < 700) {
        return Math.floor(imgWidth / (thumbnailMobile + 10))
      }
      return Math.floor(imgWidth / (thumbnailWidth + 10))
    }
  },
  watch: {
    screenWidth (from, to) {
      this.setThumbnailsToShow()
    }
  },
  mounted () {
    this.$nextTick(() => {
      // this.imageSettings.asNavFor = this.$refs?.galleryImgThumbnail
      // this.thumbnailSettings.asNavFor = this.$refs?.galleryImg
      this.setThumbnailsToShow();
    });
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    handleKeyDown (event) {
      if (this.isTransitioning) {
        return
      }
      if (event.key === 'ArrowLeft') {
        this.goToPrevious()
      } else if (event.key === 'ArrowRight') {
        this.goToNext()
      } else if (event.keyCode === 27) {
        this.emitClose()
      }
    },
    emitClose () {
      this.$emit('closeGallery', this.curIndex);
    },
    onImgInit () {
      this.$nextTick(() => {
        window.addEventListener('keydown', this.handleKeyDown)
      })
    },
    onImgBeforeChange (oldSlideIndex, newSlideIndex) {
      this.$refs?.galleryImgThumbnail.goTo(newSlideIndex)
    },
    onBeforeChange (oldSlideIndex, newSlideIndex) {
      this.curIndex = newSlideIndex
      this.$refs?.galleryImg.goTo(newSlideIndex)
    },
    goToNext () {
      if (this.isTransitioning) {
        return
      }
      this.$refs?.galleryImgThumbnail.next()
      this.isTransitioning = true
      setTimeout(() => {
        this.isTransitioning = false
      }, 200)
    },
    goToPrevious () {
      if (this.isTransitioning) {
        return
      }

      this.$refs?.galleryImgThumbnail.prev()

      this.isTransitioning = true
      setTimeout(() => {
        this.isTransitioning = false
      }, 200)
    },
    setThumbnailsToShow () {
      this.thumbnailSettings.slidesToShow = this.thumbnailsToShow
    }
  }
}
</script>
<style lang="scss" scoped>
$maxGalleryImgWidth: calc(150vh - 330px);
.photo-gallery-modal-container {
  margin: 0;
  outline: 0;
  padding: 32px 90px 16px;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  .close-button {
    background: transparent;
    border: none;
    color: rgb(255, 255, 255);
    cursor: pointer;
    height: 32px;
    line-height: 32px;
    outline: 0;
    padding: 0;
    position: absolute;
    width: 32px;
    top: -1rem;
    width: 32px;
    right: -2rem;
    box-sizing: border-box;
    z-index: 10;
    @include max-width($tablet-lg) {
      top: -2rem;
      right: 0.5rem;
    }

    span {
      align-self: center;
      display: inline-flex;
      flex-shrink: 0;
      font-style: normal;
      height: 24px;
      width: 24px;
      box-sizing: border-box;
      svg {
        fill: currentColor;
        height: 100%;
        width: 100%;
      }
    }
  }
  .single-image-container {
    margin: 0 auto 24px;
    max-width: $maxGalleryImgWidth;
    // max-height: 650px;
    position: relative;
    box-sizing: border-box;
    .next-button,
    .previous-button {
      background-color: transparent;
      background-position: 50%;
      background-repeat: no-repeat;
      background-size: 27px 44px;
      color: rgb(255, 255, 255);
      cursor: pointer;
      height: 32px;
      opacity: 0.8;
      outline: 0;
      padding: 0 24px;
      position: absolute;
      top: 50%;
      width: 27px;
      box-sizing: border-box;
      border: none;
      @media (max-width: 768px) {
        position: absolute;
        z-index: 20;
      }
    }

    .next-button {
      background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 27 44'%3E%3Cpath d='M27 22 5 44l-2.1-2.1L22.8 22 2.9 2.1 5 0l22 22z' fill='%23fff'/%3E%3C/svg%3E");
      right: -56px;
      @media (max-width: 768px) {
        right: 0;
      }
    }

    .previous-button {
      background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 27 44'%3E%3Cpath d='M0 22 22 0l2.1 2.1L4.2 22l19.9 19.9L22 44 0 22z' fill='%23fff'/%3E%3C/svg%3E");
      left: -56px;
      @media (max-width: 768px) {
        left: 0;
      }
    }

    .slider-image-container {
      margin: 0 auto;
      overflow: auto;
      // padding-bottom: 66.7%;
      position: relative;
      -webkit-user-select: none;
      -moz-user-select: none;
      user-select: none;
      width: 100%;
      box-sizing: border-box;
    }
  }
  .thumbnail-nav {
    width: 130px;
    height: 90px;
  }

  .slider-container {
    width: 100%;
    height: 100%;
  }
  .gallery-slider-wrapper {
    position: relative;
    // overflow: hidden;
  }
  .gallery-thumbnail-wrapper {
    .opaque {
      opacity: 0.5;
    }
  }

}

@include max-width($tablet-portrait) {
  .photo-gallery-modal-container {
    padding: 0;
    .single-image-container {
      margin: auto 0;
    }
  }
}
@include max-width($mobile-extra) {
  .photo-gallery-modal-container {
    .thumbnail-nav {
      width: 100px;
      height: 65px;
    }
  }
}

</style>
